/**
 * Route guard for capability-gated route GROUPS — the SPA's first route-level capability gate
 * (gate-panel plan §D). Mirrors {@link ProtectedRoute}: declarative `<Navigate>` in render
 * (R-18.181), never a `useEffect` redirect that would flash the gated content. Sits UNDER
 * `<ProtectedRoute>` in the route tree, so auth is already settled; a user who lacks the capability
 * is sent home, where the panel landing logic applies. The backend still 403s every endpoint
 * independently (PA-web-capability-gating.2) — this only keeps the UI honest.
 */

import { Navigate, Outlet } from 'react-router-dom';

import { useAuth } from '@/shared/auth/AuthProvider';

interface RequireCapabilityProps {
  capability: string;
}

export function RequireCapability({ capability }: RequireCapabilityProps): React.JSX.Element {
  const { hasCapability } = useAuth();

  if (!hasCapability(capability)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

/** The same guard for a surface several capabilities open (any one of them admits). */
export function RequireAnyCapability({ capabilities }: { capabilities: readonly string[] }): React.JSX.Element {
  const { hasCapability } = useAuth();

  if (!capabilities.some(hasCapability)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
